import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { TokenStorageService } from '../services/helpers/token.service';
import { TokenRefreshService } from '../services/token-refresh.service';
import { AccountService } from '../services/account.service';
import { SocketService } from '../services/socket.service';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private refreshFailed = false;
  private readonly ERROR_MARKER = 'ERROR' as any;

  constructor(
    private tokenService: TokenStorageService,
    private tokenRefreshService: TokenRefreshService,
    private router: Router,
    private accountService: AccountService,
    private socketService: SocketService,
    private toastrService: ToastrService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const token = this.tokenService.getToken();

    const authRequest = token
      ? request.clone({
          setHeaders: { Authorization: `Bearer ${token}` },
        })
      : request;

    return next.handle(authRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !request.url.includes('refresh')) {
          return this.handle401Error(authRequest, next);
        } else if (error.status === 422) {
          for (const key of Object.keys(error.error.errors)) {
            this.toastrService.error(error.error.errors[key]);
          }
        } else if (
          error?.error?.code === 'user-invalid-batch-user-state-exception'
        ) {
          this.toastrService.error(
            'One or more selected users are inactive. Please reactivate them before assigning.',
          );
        } else if (
          error?.error?.code !== 'auth-two-factor-code-required-exception'
        ) {
          this.toastrService.error(error?.error?.message);
        }

        return throwError(() => error);
      }),
    );
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const refreshToken = this.tokenService.getRefreshToken();

    // If no refresh token available, logout immediately
    if (!refreshToken) {
      this.logout();
      return throwError(() => new Error('No refresh token available'));
    }

    // If refresh already failed, logout immediately
    if (this.refreshFailed) {
      this.logout();
      return throwError(() => new Error('Token refresh already failed'));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.tokenRefreshService.refreshToken().pipe(
        switchMap((response: any) => {
          this.isRefreshing = false;
          this.refreshFailed = false;

          // Response structure: response.token.access_token
          const newAccessToken =
            response?.token?.access_token || response?.access_token;

          if (!newAccessToken) {
            this.refreshFailed = true;
            this.refreshTokenSubject.next(this.ERROR_MARKER);
            this.logout();
            return throwError(() => new Error('No access token returned'));
          }

          // Token is already saved by tokenRefreshService
          // Unblock all queued requests
          this.refreshTokenSubject.next(newAccessToken);
          this.socketService.reconnectAfterTokenRefresh();

          return next.handle(
            request.clone({
              setHeaders: {
                Authorization: `Bearer ${newAccessToken}`,
              },
            }),
          );
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.refreshFailed = true;
          // Notify queued requests about failure
          this.refreshTokenSubject.next(this.ERROR_MARKER);
          this.logout();
          return throwError(() => err);
        }),
      );
    }

    // If refresh is already in progress, wait for it
    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => {
        // Check if this is an error marker or refresh failed
        if (token === this.ERROR_MARKER || this.refreshFailed) {
          return throwError(() => new Error('Token refresh failed'));
        }
        return next.handle(
          request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
            },
          }),
        );
      }),
      catchError((err) => {
        return throwError(() => err);
      }),
    );
  }

  private logout() {
    const user = this.accountService.getAccount();
    localStorage.clear();
    sessionStorage.clear();
    if (user) {
      this.socketService.unSubscribe(user.id);
    }
    // Reset flags on logout
    this.isRefreshing = false;
    this.refreshFailed = false;
    this.refreshTokenSubject.next(null);
    if (globalThis.location.pathname === '/profile/sessions') {
      this.router.navigate(['/auth/login'], {
        queryParams: { next_url: '/profile/sessions' },
      });
      return;
    }
    this.router.navigate(['/auth/login']);
  }
}
